import {
    ChartType,
    convertFieldRefToFieldId,
    getFieldRef,
    getItemId,
    isSqlTableCalculation,
    lightdashVariablePattern,
    maybeReplaceFieldsInChartVersion,
    toggleArrayValue,
    updateFieldIdInFilters,
    type AdditionalMetric,
    type ChartConfig,
    type CustomDimension,
    type CustomFormat,
    type Dimension,
    type FieldId,
    type Item,
    type ItemsMap,
    type Metric,
    type MetricQuery,
    type MetricType,
    type ParameterValue,
    type ReplaceCustomFields,
    type SavedChart,
    type SortField,
    type TableCalculation,
    type TimezoneSetting,
} from '@lightdash/common';
import {
    createNextState,
    createSlice,
    current,
    type PayloadAction,
} from '@reduxjs/toolkit';
import { type QueryResultsProps } from '../../../hooks/useQueryResults';
import { defaultState } from '../../../providers/Explorer/defaultState';
import {
    type ConfigCacheMap,
    type ExplorerReduceState,
    type ExplorerSection,
    type MapExtent,
} from '../../../providers/Explorer/types';
import {
    getCachedPivotConfig,
    getValidChartConfig,
} from '../../../providers/Explorer/utils';
import { calcColumnOrder, reorderColumnOrderSlots } from './utils';

export type ExplorerSliceState = ExplorerReduceState;

// Type-safe helper to save config to cache
// Ensures that chartType and config match up correctly
function saveConfigToCache<T extends ChartType>(
    cache: Partial<ConfigCacheMap>,
    chartType: T,
    config: ConfigCacheMap[T]['chartConfig'],
    pivotConfig?: SavedChart['pivotConfig'],
): void {
    cache[chartType] = {
        chartConfig: config,
        pivotConfig,
    } as ConfigCacheMap[T];
}

// `current` rejects undefined, and a data app viz chart may carry no config.
const snapshotChartConfig = (config: ChartConfig['config']) =>
    config === undefined ? undefined : current(config);

const initialState: ExplorerSliceState = defaultState;

const removePivotValuesFromSorts = (sorts: SortField[]): SortField[] => {
    const seen = new Set<string>();
    return sorts.reduce<SortField[]>((acc, sort) => {
        if (seen.has(sort.fieldId)) return acc;
        seen.add(sort.fieldId);
        const { pivotValues: _pivotValues, ...rest } = sort;
        acc.push(rest);
        return acc;
    }, []);
};

const applyToggleDimension = (state: ExplorerSliceState, fieldId: FieldId) => {
    const currentDimensions = state.unsavedChartVersion.metricQuery.dimensions;

    state.unsavedChartVersion.metricQuery.dimensions = toggleArrayValue(
        currentDimensions,
        fieldId,
    );

    state.unsavedChartVersion.metricQuery.sorts =
        state.unsavedChartVersion.metricQuery.sorts.filter(
            (s) => s.fieldId !== fieldId,
        );

    const dimensionIds = state.unsavedChartVersion.metricQuery.dimensions;
    const metricIds = state.unsavedChartVersion.metricQuery.metrics;
    const calcIds = state.unsavedChartVersion.metricQuery.tableCalculations.map(
        ({ name }) => name,
    );

    state.unsavedChartVersion.tableConfig.columnOrder = calcColumnOrder(
        state.unsavedChartVersion.tableConfig.columnOrder,
        [...dimensionIds, ...metricIds, ...calcIds],
        dimensionIds,
    );
};

const applyToggleMetric = (state: ExplorerSliceState, fieldId: FieldId) => {
    const currentMetrics = state.unsavedChartVersion.metricQuery.metrics;

    state.unsavedChartVersion.metricQuery.metrics = toggleArrayValue(
        currentMetrics,
        fieldId,
    );

    state.unsavedChartVersion.metricQuery.sorts =
        state.unsavedChartVersion.metricQuery.sorts.filter(
            (s) => s.fieldId !== fieldId,
        );

    state.unsavedChartVersion.metricQuery.metricOverrides = Object.fromEntries(
        Object.entries(
            state.unsavedChartVersion.metricQuery.metricOverrides || {},
        ).filter(([key]) =>
            state.unsavedChartVersion.metricQuery.metrics.includes(key),
        ),
    );

    const dimensionIds = state.unsavedChartVersion.metricQuery.dimensions;
    const metricIds = state.unsavedChartVersion.metricQuery.metrics;
    const calcIds = state.unsavedChartVersion.metricQuery.tableCalculations.map(
        ({ name }) => name,
    );

    state.unsavedChartVersion.tableConfig.columnOrder = calcColumnOrder(
        state.unsavedChartVersion.tableConfig.columnOrder,
        [...dimensionIds, ...metricIds, ...calcIds],
    );
};

const explorerSlice = createSlice({
    name: 'explorer',
    initialState,
    reducers: {
        reset: (_state, action: PayloadAction<ExplorerSliceState>) => {
            return action.payload;
        },
        setTableName: (state, action: PayloadAction<string>) => {
            state.unsavedChartVersion.tableName = action.payload;
            state.unsavedChartVersion.metricQuery.exploreName = action.payload;
        },
        setIsEditMode: (state, action: PayloadAction<boolean>) => {
            state.isEditMode = action.payload;
        },
        setIsMinimal: (state, action: PayloadAction<boolean>) => {
            state.isMinimal = action.payload;
        },
        setSavedChart: (
            state,
            action: PayloadAction<SavedChart | undefined>,
        ) => {
            state.savedChart = action.payload;
            state.unsavedColorPaletteUuid =
                action.payload?.colorPaletteUuid ?? null;
        },
        // Server-owned metadata (name, pin, verification, slug...) without the
        // version or palette, so a mid-session refetch leaves staged edits
        // and the staged palette alone (setSavedChart would reset them).
        setSavedChartMetadata: (state, action: PayloadAction<SavedChart>) => {
            if (!state.savedChart) return;
            const {
                metricQuery,
                chartConfig,
                tableConfig,
                pivotConfig,
                parameters,
                colorPaletteUuid,
                merge,
                ...metadata
            } = action.payload;
            state.savedChart = { ...state.savedChart, ...metadata };
        },
        setColorPaletteUuid: (state, action: PayloadAction<string | null>) => {
            state.unsavedColorPaletteUuid = action.payload;
        },
        setPreviouslyFetchedState: (
            state,
            action: PayloadAction<MetricQuery>,
        ) => {
            state.previouslyFetchedState = action.payload;

            // Clear table calculation metadata now that query has successfully run
            // The new query results will have the updated table calculation names
            if (state.metadata?.tableCalculations) {
                state.metadata.tableCalculations = [];
            }
        },

        toggleExpandedSection: (
            state,
            action: PayloadAction<ExplorerSection>,
        ) => {
            const index = state.expandedSections.indexOf(action.payload);
            if (index > -1) {
                state.expandedSections.splice(index, 1);
            } else {
                state.expandedSections.push(action.payload);
            }
        },

        setFilters: (state, action: PayloadAction<MetricQuery['filters']>) => {
            state.unsavedChartVersion.metricQuery.filters = action.payload;
        },

        setSortFields: (state, action: PayloadAction<SortField[]>) => {
            state.unsavedChartVersion.metricQuery.sorts = action.payload;
        },

        setDimensions: (state, action: PayloadAction<string[]>) => {
            state.unsavedChartVersion.metricQuery.dimensions = action.payload;
        },
        setMetrics: (state, action: PayloadAction<string[]>) => {
            state.unsavedChartVersion.metricQuery.metrics = action.payload;
        },

        toggleDimension: (state, action: PayloadAction<FieldId>) => {
            applyToggleDimension(state, action.payload);
        },

        toggleMetric: (state, action: PayloadAction<FieldId>) => {
            applyToggleMetric(state, action.payload);
        },

        // Config-panel "Add to query" pickers: never deselect, and always run
        // the query — itemsMap only refreshes from results, even when
        // auto-fetch is off.
        addDimensionToQuery: (state, action: PayloadAction<FieldId>) => {
            if (
                !state.unsavedChartVersion.metricQuery.dimensions.includes(
                    action.payload,
                )
            ) {
                applyToggleDimension(state, action.payload);
            }
            state.queryExecution.pendingFetch = true;
        },

        addMetricToQuery: (state, action: PayloadAction<FieldId>) => {
            if (
                !state.unsavedChartVersion.metricQuery.metrics.includes(
                    action.payload,
                )
            ) {
                applyToggleMetric(state, action.payload);
            }
            state.queryExecution.pendingFetch = true;
        },

        removeField: (state, action: PayloadAction<FieldId>) => {
            const fieldToRemove = action.payload;

            state.unsavedChartVersion.metricQuery.dimensions =
                state.unsavedChartVersion.metricQuery.dimensions.filter(
                    (fieldId) => fieldId !== fieldToRemove,
                );

            state.unsavedChartVersion.metricQuery.metrics =
                state.unsavedChartVersion.metricQuery.metrics.filter(
                    (fieldId) => fieldId !== fieldToRemove,
                );

            state.unsavedChartVersion.metricQuery.sorts =
                state.unsavedChartVersion.metricQuery.sorts.filter(
                    (s) => s.fieldId !== fieldToRemove,
                );

            state.unsavedChartVersion.metricQuery.tableCalculations =
                state.unsavedChartVersion.metricQuery.tableCalculations.filter(
                    (tc) => tc.name !== fieldToRemove,
                );

            state.unsavedChartVersion.tableConfig.columnOrder =
                state.unsavedChartVersion.tableConfig.columnOrder.filter(
                    (fieldId) => fieldId !== fieldToRemove,
                );

            state.unsavedChartVersion.metricQuery.customDimensions = (
                state.unsavedChartVersion.metricQuery.customDimensions || []
            ).filter((cd) => cd.id !== fieldToRemove);

            state.unsavedChartVersion.metricQuery.additionalMetrics = (
                state.unsavedChartVersion.metricQuery.additionalMetrics || []
            ).filter((am) => getItemId(am) !== fieldToRemove);
        },

        setRowLimit: (state, action: PayloadAction<number>) => {
            state.unsavedChartVersion.metricQuery.limit = action.payload;
        },

        setTimeZone: (
            state,
            action: PayloadAction<TimezoneSetting | undefined>,
        ) => {
            state.unsavedChartVersion.metricQuery.timezone = action.payload;
        },

        setColumnOrder: (state, action: PayloadAction<string[]>) => {
            state.unsavedChartVersion.tableConfig.columnOrder = action.payload;
        },

        setPivotConfig: (
            state,
            action: PayloadAction<SavedChart['pivotConfig']>,
        ) => {
            state.unsavedChartVersion.pivotConfig = action.payload;
            if (!action.payload?.columns.length) {
                state.unsavedChartVersion.metricQuery.sorts =
                    removePivotValuesFromSorts(
                        state.unsavedChartVersion.metricQuery.sorts,
                    );
            }
        },

        setPivotColumns: (state, action: PayloadAction<string[]>) => {
            const rows = state.unsavedChartVersion.pivotConfig?.rows;
            state.unsavedChartVersion.pivotConfig = {
                columns: action.payload,
                ...(rows !== undefined && { rows }),
            };

            if (!action.payload.length) {
                state.unsavedChartVersion.metricQuery.sorts =
                    removePivotValuesFromSorts(
                        state.unsavedChartVersion.metricQuery.sorts,
                    );
            }
        },

        setPivotRows: (state, action: PayloadAction<string[] | undefined>) => {
            const columns =
                state.unsavedChartVersion.pivotConfig?.columns ?? [];
            // Without pivot columns the table follows columnOrder, so reorder it instead
            if (columns.length === 0 && action.payload !== undefined) {
                state.unsavedChartVersion.tableConfig.columnOrder =
                    reorderColumnOrderSlots(
                        state.unsavedChartVersion.tableConfig.columnOrder,
                        action.payload,
                    );
                state.unsavedChartVersion.pivotConfig = { columns };
                return;
            }
            state.unsavedChartVersion.pivotConfig = {
                columns,
                ...(action.payload !== undefined && { rows: action.payload }),
            };
        },

        setParameter: (
            state,
            action: PayloadAction<{
                key: string;
                value: ParameterValue | null;
            }>,
        ) => {
            const { key, value } = action.payload;
            if (!state.unsavedChartVersion.parameters) {
                state.unsavedChartVersion.parameters = {};
            }
            if (value === null || value === undefined) {
                delete state.unsavedChartVersion.parameters[key];
            } else {
                state.unsavedChartVersion.parameters[key] = value;
            }
        },

        clearAllParameters: (state) => {
            state.unsavedChartVersion.parameters = {};
        },

        setParameterReferences: (
            state,
            action: PayloadAction<string[] | null>,
        ) => {
            state.parameterReferences = action.payload;
        },

        setParameterDefinitions: (
            state,
            action: PayloadAction<ExplorerReduceState['parameterDefinitions']>,
        ) => {
            state.parameterDefinitions = action.payload;
        },

        openVisualizationConfig: (state) => {
            state.isVisualizationConfigOpen = true;
        },
        closeVisualizationConfig: (state) => {
            state.isVisualizationConfigOpen = false;
            state.chartSidebarStep = 'configure';
        },
        setChartSidebarStep: (
            state,
            action: PayloadAction<ExplorerSliceState['chartSidebarStep']>,
        ) => {
            state.chartSidebarStep = action.payload;
        },

        // A new type has no viz to point at until its first version lands;
        // what was there before is kept so cancelling restores it.
        startChartTypeAuthoring: (
            state,
            action: PayloadAction<{ dataAppVizUuid: string | null }>,
        ) => {
            const before = state.unsavedChartVersion.chartConfig;
            const beforePivotConfig = state.unsavedChartVersion.pivotConfig;
            state.chartTypeAuthoring = {
                dataAppVizUuid: action.payload.dataAppVizUuid,
                viewedVersion: null,
                createdInSession: false,
                previous: {
                    chartSidebarStep: state.chartSidebarStep,
                    chartConfig: current(before),
                    pivotConfig: beforePivotConfig
                        ? (current(
                              beforePivotConfig,
                          ) as SavedChart['pivotConfig'])
                        : undefined,
                },
            };
            state.chartSidebarStep = 'configure';
            state.isVisualizationConfigOpen = true;
            if (action.payload.dataAppVizUuid === null) {
                saveConfigToCache(
                    state.cachedChartConfigs,
                    before.type,
                    snapshotChartConfig(before.config),
                    beforePivotConfig
                        ? (current(
                              beforePivotConfig,
                          ) as SavedChart['pivotConfig'])
                        : undefined,
                );
                state.unsavedChartVersion.chartConfig = {
                    type: ChartType.DATA_APP_VIZ,
                };
                state.unsavedChartVersion.pivotConfig = undefined;
            }
        },
        // The builder pinned an older version (or returned to the current
        // one), so the sidebar configures what the preview renders.
        viewChartTypeAuthoringVersion: (
            state,
            action: PayloadAction<number | null>,
        ) => {
            if (state.chartTypeAuthoring === null) return;
            state.chartTypeAuthoring.viewedVersion = action.payload;
        },
        // A first build claimed an app; the session keeps going under it.
        claimChartTypeAuthoringViz: (state, action: PayloadAction<string>) => {
            if (state.chartTypeAuthoring?.dataAppVizUuid !== null) return;
            state.chartTypeAuthoring.dataAppVizUuid = action.payload;
            state.chartTypeAuthoring.createdInSession = true;
        },
        // Back to the chart and the sidebar step as they were.
        cancelChartTypeAuthoring: (state) => {
            const authoring = state.chartTypeAuthoring;
            if (authoring === null) return;
            // The query stayed live meanwhile, so the pivot only keeps the
            // dimensions it still has.
            const dimensions = new Set(
                state.unsavedChartVersion.metricQuery.dimensions,
            );
            const pivot = authoring.previous.pivotConfig;
            const columns = pivot?.columns.filter((c) => dimensions.has(c));
            state.unsavedChartVersion.chartConfig =
                authoring.previous.chartConfig;
            state.unsavedChartVersion.pivotConfig =
                pivot && columns && columns.length > 0
                    ? { ...pivot, columns }
                    : undefined;
            state.chartSidebarStep = authoring.previous.chartSidebarStep;
            state.chartTypeAuthoring = null;
            state.isVisualizationConfigOpen = true;
        },
        // Return edits to their entry point; a new type needs configuration.
        finishChartTypeAuthoring: (state) => {
            const authoring = state.chartTypeAuthoring;
            if (authoring === null) return;
            state.chartSidebarStep =
                authoring.dataAppVizUuid !== null && !authoring.createdInSession
                    ? authoring.previous.chartSidebarStep
                    : 'configure';
            state.chartTypeAuthoring = null;
            state.isVisualizationConfigOpen = true;
        },

        toggleCustomDimensionModal: (
            state,
            action: PayloadAction<
                | {
                      isEditing: boolean;
                      table?: string;
                      item?: Dimension | CustomDimension;
                  }
                | undefined
            >,
        ) => {
            state.modals.customDimension = {
                isOpen: !state.modals.customDimension.isOpen,
                ...(action.payload && { ...action.payload }),
            };
        },
        toggleAdditionalMetricModal: (
            state,
            action: PayloadAction<
                | {
                      type?: MetricType;
                      item?:
                          | Dimension
                          | AdditionalMetric
                          | CustomDimension
                          | Metric;
                      isEditing: boolean;
                      /** Label typed before handing over from quick create */
                      label?: string;
                      /** Merge source that owns this custom metric. */
                      mergeSourceId?: string;
                  }
                | undefined
            >,
        ) => {
            state.modals.additionalMetric = {
                isOpen: !state.modals.additionalMetric.isOpen,
                ...(action.payload && { ...action.payload }),
            };
        },
        togglePeriodOverPeriodComparisonModal: (
            state,
            action: PayloadAction<
                { metric?: Metric; itemsMap?: ItemsMap } | undefined
            >,
        ) => {
            state.modals.periodOverPeriodComparison = {
                isOpen: !state.modals.periodOverPeriodComparison.isOpen,
                ...(action.payload && { ...action.payload }),
            };
        },
        toggleWriteBackModal: (
            state,
            action: PayloadAction<
                { items?: CustomDimension[] | AdditionalMetric[] } | undefined
            >,
        ) => {
            state.modals.writeBack = {
                isOpen: !state.modals.writeBack.isOpen,
                ...(action.payload && { ...action.payload }),
            };
        },
        toggleFormatModal: (
            state,
            action: PayloadAction<{ item?: Metric | Dimension } | undefined>,
        ) => {
            state.modals.format = {
                isOpen: !state.modals.format.isOpen,
                ...(action.payload && { ...action.payload }),
            };
        },
        updateMetricFormat: (
            state,
            action: PayloadAction<{
                metric: Metric;
                formatOptions: CustomFormat | undefined;
            }>,
        ) => {
            const { metric, formatOptions } = action.payload;
            const metricId = getItemId(metric);
            if (!state.unsavedChartVersion.metricQuery.metricOverrides) {
                state.unsavedChartVersion.metricQuery.metricOverrides = {};
            }
            state.unsavedChartVersion.metricQuery.metricOverrides[metricId] = {
                formatOptions,
            };

            // Propagate format override to any period-over-period metrics
            // derived from this base metric
            const additionalMetrics =
                state.unsavedChartVersion.metricQuery.additionalMetrics;
            if (additionalMetrics) {
                for (const am of additionalMetrics) {
                    if (
                        am.baseMetricId === metricId &&
                        am.generationType === 'periodOverPeriod'
                    ) {
                        const popMetricId = getItemId(am);
                        state.unsavedChartVersion.metricQuery.metricOverrides[
                            popMetricId
                        ] = { formatOptions };
                    }
                }
            }
        },
        updateDimensionFormat: (
            state,
            action: PayloadAction<{
                dimension: Dimension;
                formatOptions: CustomFormat | undefined;
            }>,
        ) => {
            const { dimension, formatOptions } = action.payload;
            const dimensionId = getItemId(dimension);
            if (!state.unsavedChartVersion.metricQuery.dimensionOverrides) {
                state.unsavedChartVersion.metricQuery.dimensionOverrides = {};
            }
            state.unsavedChartVersion.metricQuery.dimensionOverrides[
                dimensionId
            ] = {
                formatOptions,
            };
        },
        openItemDetail: (
            state,
            action: PayloadAction<{
                itemType: 'field' | 'table' | 'group';
                label: string;
                description?: string;
                fieldItem?: Item | AdditionalMetric;
                tableMetadata?: {
                    name: string;
                    dbtPackageName?: string;
                    ymlPath?: string;
                    sqlPath?: string;
                };
            }>,
        ) => {
            state.modals.itemDetail = {
                isOpen: true,
                itemType: action.payload.itemType,
                label: action.payload.label,
                description: action.payload.description,
                fieldItem: action.payload.fieldItem,
                tableMetadata: action.payload.tableMetadata,
            };
        },
        closeItemDetail: (state) => {
            state.modals.itemDetail = {
                isOpen: false,
                itemType: undefined,
                label: undefined,
                description: undefined,
                fieldItem: undefined,
                tableMetadata: undefined,
            };
        },

        setChartType: (
            state,
            action: PayloadAction<{ chartType: ChartType }>,
        ) => {
            const before = state.unsavedChartVersion.chartConfig;
            const beforePivotConfig = state.unsavedChartVersion.pivotConfig;

            // save the current config and pivotConfig to the cache
            saveConfigToCache(
                state.cachedChartConfigs,
                before.type,
                snapshotChartConfig(before.config),
                beforePivotConfig
                    ? (current(beforePivotConfig) as SavedChart['pivotConfig'])
                    : undefined,
            );

            // take a plain snapshot of the cache to avoid passing drafts
            const plainCache = current(
                state.cachedChartConfigs,
            ) as Partial<ConfigCacheMap>;

            // restore the chartConfig
            state.unsavedChartVersion.chartConfig = getValidChartConfig(
                action.payload.chartType,
                plainCache,
            );

            // restore the pivotConfig
            state.unsavedChartVersion.pivotConfig = getCachedPivotConfig(
                action.payload.chartType,
                plainCache,
            );
        },

        setChartConfig: (
            state,
            action: PayloadAction<{
                chartConfig: ChartConfig;
            }>,
        ) => {
            state.unsavedChartVersion.chartConfig = getValidChartConfig(
                action.payload.chartConfig.type,
                state.cachedChartConfigs,
                action.payload.chartConfig,
            );
        },

        // Table calculation changes must run even when auto-fetch is disabled,
        // so a broken calculation can recover as soon as it is fixed or removed.
        addTableCalculation: (
            state,
            action: PayloadAction<TableCalculation>,
        ) => {
            state.queryExecution.pendingFetch = true;
            state.unsavedChartVersion.metricQuery.tableCalculations.push(
                action.payload,
            );

            // Update columnOrder to include new table calculation
            const dimensionIds =
                state.unsavedChartVersion.metricQuery.dimensions;
            const metricIds = state.unsavedChartVersion.metricQuery.metrics;
            const calcIds =
                state.unsavedChartVersion.metricQuery.tableCalculations.map(
                    ({ name }) => name,
                );

            state.unsavedChartVersion.tableConfig.columnOrder = calcColumnOrder(
                state.unsavedChartVersion.tableConfig.columnOrder,
                [...dimensionIds, ...metricIds, ...calcIds],
            );
        },
        updateTableCalculation: (
            state,
            action: PayloadAction<{
                oldName: string;
                tableCalculation: TableCalculation;
            }>,
        ) => {
            const { oldName, tableCalculation } = action.payload;
            const newName = tableCalculation.name;

            state.queryExecution.pendingFetch = true;

            // Update metadata to track the name change, this is used
            // by consuming visualizations to translate old field names
            // to new field names until the query re-runs
            if (!state.metadata) {
                state.metadata = {};
            }
            const tcMetadataIndex =
                state.metadata.tableCalculations?.findIndex(
                    (tc) => tc.name === oldName,
                ) ?? -1;

            if (tcMetadataIndex >= 0 && state.metadata.tableCalculations) {
                state.metadata.tableCalculations[tcMetadataIndex] = {
                    name: newName,
                    oldName,
                };
            } else {
                state.metadata.tableCalculations = [
                    ...(state.metadata.tableCalculations ?? []),
                    { name: newName, oldName },
                ];
            }

            // Update table calculation
            const index =
                state.unsavedChartVersion.metricQuery.tableCalculations.findIndex(
                    (tc) => tc.name === oldName,
                );
            if (index > -1) {
                state.unsavedChartVersion.metricQuery.tableCalculations[index] =
                    tableCalculation;
            }

            // Update sorts to use new name
            state.unsavedChartVersion.metricQuery.sorts =
                state.unsavedChartVersion.metricQuery.sorts.map((field) =>
                    field.fieldId === oldName
                        ? { ...field, fieldId: newName }
                        : field,
                );

            // Update column order to use new name
            state.unsavedChartVersion.tableConfig.columnOrder =
                state.unsavedChartVersion.tableConfig.columnOrder.map(
                    (column) => (column === oldName ? newName : column),
                );
        },
        deleteTableCalculation: (state, action: PayloadAction<string>) => {
            const nameToRemove = action.payload;

            state.queryExecution.pendingFetch = true;

            // Remove from metadata if it exists
            if (state.metadata?.tableCalculations) {
                state.metadata.tableCalculations =
                    state.metadata.tableCalculations.filter(
                        (tc) => tc.name !== nameToRemove,
                    );
            }

            state.unsavedChartVersion.metricQuery.tableCalculations =
                state.unsavedChartVersion.metricQuery.tableCalculations.filter(
                    (tc) => tc.name !== nameToRemove,
                );

            // Remove any sorts referencing this table calculation
            state.unsavedChartVersion.metricQuery.sorts =
                state.unsavedChartVersion.metricQuery.sorts.filter(
                    (sort) => sort.fieldId !== nameToRemove,
                );

            // Recalculate columnOrder to remove deleted table calculation
            const dimensionIds =
                state.unsavedChartVersion.metricQuery.dimensions;
            const metricIds = state.unsavedChartVersion.metricQuery.metrics;
            const calcIds =
                state.unsavedChartVersion.metricQuery.tableCalculations.map(
                    ({ name }) => name,
                );

            state.unsavedChartVersion.tableConfig.columnOrder = calcColumnOrder(
                state.unsavedChartVersion.tableConfig.columnOrder,
                [...dimensionIds, ...metricIds, ...calcIds],
            );
        },
        setTableCalculations: (
            state,
            action: PayloadAction<TableCalculation[]>,
        ) => {
            state.unsavedChartVersion.metricQuery.tableCalculations =
                action.payload;
        },

        addCustomDimension: (state, action: PayloadAction<CustomDimension>) => {
            const newCustomDimension = action.payload;

            if (!state.unsavedChartVersion.metricQuery.customDimensions) {
                state.unsavedChartVersion.metricQuery.customDimensions = [];
            }
            state.unsavedChartVersion.metricQuery.customDimensions.push(
                newCustomDimension,
            );

            const customDimensionId = getItemId(newCustomDimension);
            if (
                !state.unsavedChartVersion.metricQuery.dimensions.includes(
                    customDimensionId,
                )
            ) {
                state.unsavedChartVersion.metricQuery.dimensions.push(
                    customDimensionId,
                );
            }

            const dimensionIds =
                state.unsavedChartVersion.metricQuery.dimensions;
            const metricIds = state.unsavedChartVersion.metricQuery.metrics;
            const calcIds =
                state.unsavedChartVersion.metricQuery.tableCalculations.map(
                    ({ name }) => name,
                );

            state.unsavedChartVersion.tableConfig.columnOrder = calcColumnOrder(
                state.unsavedChartVersion.tableConfig.columnOrder,
                [...dimensionIds, ...metricIds, ...calcIds],
                dimensionIds,
            );
        },
        updateCustomDimension: (
            state,
            action: PayloadAction<{
                oldId: string;
                customDimension: CustomDimension;
            }>,
        ) => {
            const { oldId, customDimension } = action.payload;
            if (!state.unsavedChartVersion.metricQuery.customDimensions) {
                state.unsavedChartVersion.metricQuery.customDimensions = [];
                return;
            }

            const index =
                state.unsavedChartVersion.metricQuery.customDimensions.findIndex(
                    (cd) => cd.id === oldId,
                );
            if (index > -1) {
                state.unsavedChartVersion.metricQuery.customDimensions[index] =
                    customDimension;
            }

            const newId = getItemId(customDimension);
            if (oldId !== newId) {
                state.unsavedChartVersion.metricQuery.dimensions =
                    state.unsavedChartVersion.metricQuery.dimensions.map(
                        (dim) => (dim === oldId ? newId : dim),
                    );

                state.unsavedChartVersion.metricQuery.sorts =
                    state.unsavedChartVersion.metricQuery.sorts.map((sort) =>
                        sort.fieldId === oldId
                            ? { ...sort, fieldId: newId }
                            : sort,
                    );

                state.unsavedChartVersion.tableConfig.columnOrder =
                    state.unsavedChartVersion.tableConfig.columnOrder.map(
                        (col) => (col === oldId ? newId : col),
                    );
            }
        },
        removeCustomDimension: (state, action: PayloadAction<string>) => {
            const idToRemove = action.payload;
            if (!state.unsavedChartVersion.metricQuery.customDimensions) {
                return;
            }
            state.unsavedChartVersion.metricQuery.customDimensions =
                state.unsavedChartVersion.metricQuery.customDimensions.filter(
                    (cd) => cd.id !== idToRemove,
                );

            state.unsavedChartVersion.metricQuery.dimensions =
                state.unsavedChartVersion.metricQuery.dimensions.filter(
                    (dimension) => dimension !== idToRemove,
                );

            state.unsavedChartVersion.metricQuery.sorts =
                state.unsavedChartVersion.metricQuery.sorts.filter(
                    (sort) => sort.fieldId !== idToRemove,
                );

            state.unsavedChartVersion.tableConfig.columnOrder =
                state.unsavedChartVersion.tableConfig.columnOrder.filter(
                    (fieldId) => fieldId !== idToRemove,
                );
        },
        setCustomDimensions: (
            state,
            action: PayloadAction<CustomDimension[] | undefined>,
        ) => {
            state.unsavedChartVersion.metricQuery.customDimensions =
                action.payload;
        },
        addAdditionalMetric: (
            state,
            action: PayloadAction<AdditionalMetric>,
        ) => {
            const newMetric = action.payload;
            const isMetricAlreadyInList = (
                state.unsavedChartVersion.metricQuery.additionalMetrics || []
            ).find((metric) => getItemId(metric) === getItemId(newMetric));

            if (!isMetricAlreadyInList) {
                state.unsavedChartVersion.metricQuery.additionalMetrics = [
                    ...(state.unsavedChartVersion.metricQuery
                        .additionalMetrics || []),
                    newMetric,
                ];
            }

            const metricId = getItemId(newMetric);
            if (
                !state.unsavedChartVersion.metricQuery.metrics.includes(
                    metricId,
                )
            ) {
                state.unsavedChartVersion.metricQuery.metrics.push(metricId);
            }

            const dimensionIds =
                state.unsavedChartVersion.metricQuery.dimensions;
            const metricIds = state.unsavedChartVersion.metricQuery.metrics;
            const calcIds =
                state.unsavedChartVersion.metricQuery.tableCalculations.map(
                    ({ name }) => name,
                );

            state.unsavedChartVersion.tableConfig.columnOrder = calcColumnOrder(
                state.unsavedChartVersion.tableConfig.columnOrder,
                [...dimensionIds, ...metricIds, ...calcIds],
            );
        },
        // Context-compatible editAdditionalMetric with full logic including filters and table calculations
        editAdditionalMetric: (
            state,
            action: PayloadAction<{
                additionalMetric: AdditionalMetric;
                previousAdditionalMetricName: string;
            }>,
        ) => {
            const { additionalMetric, previousAdditionalMetricName } =
                action.payload;
            const additionalMetricFieldId = getItemId(additionalMetric);

            // Update metrics array
            state.unsavedChartVersion.metricQuery.metrics =
                state.unsavedChartVersion.metricQuery.metrics.map((metric) =>
                    metric === previousAdditionalMetricName
                        ? additionalMetricFieldId
                        : metric,
                );

            // Update additionalMetrics array
            state.unsavedChartVersion.metricQuery.additionalMetrics =
                state.unsavedChartVersion.metricQuery.additionalMetrics?.map(
                    (metric) =>
                        metric.uuid === additionalMetric.uuid
                            ? additionalMetric
                            : metric,
                );

            // Update sorts
            state.unsavedChartVersion.metricQuery.sorts =
                state.unsavedChartVersion.metricQuery.sorts.map((sortField) =>
                    sortField.fieldId === previousAdditionalMetricName
                        ? {
                              ...sortField,
                              fieldId: additionalMetricFieldId,
                          }
                        : sortField,
                );

            // Update filters
            const newFilters = {
                ...state.unsavedChartVersion.metricQuery.filters,
            };
            if (newFilters.metrics) {
                updateFieldIdInFilters(
                    newFilters.metrics,
                    previousAdditionalMetricName,
                    additionalMetricFieldId,
                );
            }
            state.unsavedChartVersion.metricQuery.filters = newFilters;

            // Update tableCalculations SQL references
            const tableCalcNames = new Set(
                state.unsavedChartVersion.metricQuery.tableCalculations.map(
                    (tc) => tc.name,
                ),
            );

            state.unsavedChartVersion.metricQuery.tableCalculations =
                state.unsavedChartVersion.metricQuery.tableCalculations.map(
                    (tableCalculation) => {
                        if (!isSqlTableCalculation(tableCalculation)) {
                            return tableCalculation;
                        }

                        const newSql = tableCalculation.sql.replace(
                            lightdashVariablePattern,
                            (_, fieldRef) => {
                                // Skip table calculation references - they're valid without table prefix
                                if (tableCalcNames.has(fieldRef)) {
                                    return `\${${fieldRef}}`;
                                }

                                const fieldId =
                                    convertFieldRefToFieldId(fieldRef);
                                if (fieldId === previousAdditionalMetricName) {
                                    return `\${${getFieldRef(
                                        additionalMetric,
                                    )}}`;
                                }
                                return `\${${fieldRef}}`;
                            },
                        );
                        return {
                            ...tableCalculation,
                            sql: newSql,
                        };
                    },
                );

            // Update columnOrder
            state.unsavedChartVersion.tableConfig.columnOrder =
                state.unsavedChartVersion.tableConfig.columnOrder.map(
                    (fieldId) =>
                        fieldId === previousAdditionalMetricName
                            ? additionalMetricFieldId
                            : fieldId,
                );
        },
        removeAdditionalMetric: (state, action: PayloadAction<string>) => {
            const metricIdToRemove = action.payload;

            state.unsavedChartVersion.metricQuery.additionalMetrics = (
                state.unsavedChartVersion.metricQuery.additionalMetrics || []
            ).filter((metric) => getItemId(metric) !== metricIdToRemove);

            state.unsavedChartVersion.metricQuery.metrics =
                state.unsavedChartVersion.metricQuery.metrics.filter(
                    (metric) => metric !== metricIdToRemove,
                );

            state.unsavedChartVersion.metricQuery.sorts =
                state.unsavedChartVersion.metricQuery.sorts.filter(
                    (sort) => sort.fieldId !== metricIdToRemove,
                );

            state.unsavedChartVersion.tableConfig.columnOrder =
                state.unsavedChartVersion.tableConfig.columnOrder.filter(
                    (fieldId) => fieldId !== metricIdToRemove,
                );
        },

        setValidQueryArgs: (
            state,
            action: PayloadAction<QueryResultsProps | null>,
        ) => {
            state.queryExecution.validQueryArgs = action.payload;
        },
        setUnpivotedQueryArgs: (
            state,
            action: PayloadAction<QueryResultsProps | null>,
        ) => {
            state.queryExecution.unpivotedQueryArgs = action.payload;
        },
        setQueryUuidHistory: (state, action: PayloadAction<string[]>) => {
            state.queryExecution.queryUuidHistory = action.payload;
        },
        addQueryUuid: (state, action: PayloadAction<string>) => {
            state.queryExecution.queryUuidHistory.push(action.payload);
        },
        setUnpivotedQueryUuidHistory: (
            state,
            action: PayloadAction<string[]>,
        ) => {
            state.queryExecution.unpivotedQueryUuidHistory = action.payload;
        },
        addUnpivotedQueryUuid: (state, action: PayloadAction<string>) => {
            state.queryExecution.unpivotedQueryUuidHistory.push(action.payload);
        },
        resetQueryExecution: (state) => {
            state.queryExecution = {
                validQueryArgs: null,
                unpivotedQueryArgs: null,
                queryUuidHistory: [],
                unpivotedQueryUuidHistory: [],
                pendingFetch: false,
            };
        },

        // Request a query execution (works regardless of auto-fetch setting)
        requestQueryExecution: (state) => {
            state.queryExecution.pendingFetch = true;
        },
        clearPendingFetch: (state) => {
            state.queryExecution.pendingFetch = false;
        },

        setPreAggregateCheck: (
            state,
            action: PayloadAction<ExplorerSliceState['preAggregate']['check']>,
        ) => {
            state.preAggregate.check = action.payload;
        },
        setPreAggCacheEnabled: (state, action: PayloadAction<boolean>) => {
            state.preAggregate.usePreAggregateCache = action.payload;
        },

        replaceFields: (
            state,
            action: PayloadAction<{
                fieldsToReplace: ReplaceCustomFields[string];
            }>,
        ) => {
            const { hasChanges, chartVersion } =
                maybeReplaceFieldsInChartVersion({
                    fieldsToReplace: action.payload.fieldsToReplace,
                    chartVersion: state.unsavedChartVersion,
                });
            if (hasChanges) {
                state.unsavedChartVersion = chartVersion;
            }
        },

        // Convenience action: Clear query but preserve tableName
        // Note: Components should also handle navigation side effects
        clearQuery: (
            state,
            {
                payload: { defaultState: d, tableName },
            }: PayloadAction<{
                defaultState: ExplorerSliceState;
                tableName: string;
            }>,
        ) => {
            const {
                isEditMode,
                isMinimal,
                chartSidebarStep,
                chartTypeAuthoring,
            } = state;
            return createNextState(d, (draft: ExplorerSliceState) => {
                draft.unsavedChartVersion.tableName = tableName;
                draft.unsavedChartVersion.metricQuery.exploreName = tableName;
                draft.isEditMode = isEditMode;
                draft.isMinimal = isMinimal;
                draft.chartSidebarStep = chartSidebarStep;
                draft.chartTypeAuthoring = chartTypeAuthoring;
            });
        },

        // Map extent - updated on pan/zoom, read at save time
        // Stored in cachedChartConfigs[MAP] to keep map-specific state together
        // This does NOT cause re-renders because nothing subscribes to it during render
        setMapExtent: (state, action: PayloadAction<MapExtent | null>) => {
            // Ensure the MAP cache exists
            if (!state.cachedChartConfigs[ChartType.MAP]) {
                state.cachedChartConfigs[ChartType.MAP] = {
                    chartConfig: undefined as any,
                };
            }
            state.cachedChartConfigs[ChartType.MAP].tempMapExtent =
                action.payload;
        },
    },
});

export const explorerActions = explorerSlice.actions;
export const explorerReducer = explorerSlice.reducer;
